import React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import cn from './cn';

/**
 * Radix Accordion.
 *
 * Replaced react-bootstrap's Accordion, which the menu editor drove by reading
 * `AccordionContext` and calling `useAccordionButton` from a sibling component. Radix has
 * no equivalent hook, and it turned out not to need one: `AccordionTrigger` carries
 * `data-state`, so `LinkAccordionToggle` swaps its label in CSS and holds no state at all.
 *
 * `chevron={false}` exists for that call site: a disclosure styled as a text link should
 * not carry a panel header's affordance.
 */
export const Accordion = AccordionPrimitive.Root;

export const AccordionItem = React.forwardRef(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn('tw-border-0 tw-border-b tw-border-solid tw-border-line-light', className)}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

export const AccordionTrigger = React.forwardRef(
  ({ className, children, chevron = true, ...props }, ref) => (
    <AccordionPrimitive.Header className="tw-flex tw-m-0">
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(
          'tw-flex tw-flex-1 tw-items-center tw-justify-between tw-gap-2',
          'tw-bg-transparent tw-border-0 tw-px-0 tw-py-3 tw-text-left',
          'tw-font-sans tw-text-body tw-font-bold tw-text-ink tw-cursor-pointer',
          'hover:tw-text-brand [&[data-state=open]>svg]:tw-rotate-180',
          'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand-focus',
          className
        )}
        {...props}
      >
        {children}
        {chevron && (
          <ChevronDown size={16} className="tw-shrink-0 tw-transition-transform tw-duration-150" />
        )}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
);
AccordionTrigger.displayName = 'AccordionTrigger';

export const AccordionContent = React.forwardRef(({ className, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn('tw-overflow-hidden tw-pb-3 tw-text-body', className)}
    {...props}
  />
));
AccordionContent.displayName = 'AccordionContent';

export default Accordion;
